import React, { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import { Box, Grid, IconButton, styled, Switch, switchClasses, Typography, useMediaQuery } from "@mui/material";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import { AddCircleOutline, Close, Delete, EditOutlined } from "@mui/icons-material";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { fetchActivePracticeProvidersData, usePracticeProvidersSlice } from "../../../redux/slice/practice/practiceProvidersSlice";
import { fetchPracticeUserDataByPracticeId, usePracticeEditSlice } from "../../../redux/slice/practice/practiceEditSlice";
import { setSingleAlertObj, SingleAlertInfo } from "../../../redux/slice/commonSlice";
import CustomIconButton from "../../common/CustomIconButton";
import { useTheme } from "@mui/material/styles";
import { useModalStyles } from "../../common/useModalStyles";
import { Users } from "lucide-react";
import { FieldLabel } from "../../../utils/Utility";

type AuthorisedUserInfo = {
  providerId: number | null;
  authorisedUserList: { userId: number }[];
  isNotificationEnabled?: boolean;
};
type PopupMode = 'create' | 'edit';

interface AuthorisedUserPopupProps {
  open: boolean;
  onClose: () => void;
  practiceId?: number;
  mode: PopupMode;                      // NEW
  initialValues?: any;                  // NEW
  onCreate: (payload: any) => void;     // renamed from addAuthorizedUser
  onUpdate: (id: number, payload: any) => void; // NEW
}

const AuthorizedUserPopup: React.FC<AuthorisedUserPopupProps> = ({ open, onClose, onCreate, onUpdate, initialValues, mode, practiceId }) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.mode === "dark";
  const {
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    closeIconSx,
    bodySx,
    footerSx,
    fieldWrapSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();
  const { practiceProvidersInfo } = usePracticeProvidersSlice();
  const { practiceUsersData } = usePracticeEditSlice();
  const { control, handleSubmit, watch, getValues, reset } = useForm<any>({
    defaultValues: { providerId: null, users: [{ userId: null, isNotificationEnabled: false }] },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'users' }); // [5]

  const canRemove = fields.length > 1; // hide delete if only one row
  const addEmptyRow = () => append({ userId: null, isNotificationEnabled: false }); // [3]
  const onRemove = (idx: number) => remove(idx);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && initialValues) {
      reset({
        providerAuthorizedUserId: initialValues.providerAuthorizedUserId ?? null,
        providerId: initialValues.providerId ?? null,
        users: initialValues.users?.length
          ? initialValues.users
          : [{ userId: null, isNotificationEnabled: false }],
      });
    } else {
      reset({ providerId: null, users: [{ userId: null, isNotificationEnabled: false }] });
    }
  }, [open, mode, initialValues, reset]);

  useEffect(() => {
    if (practiceId) {
      dispatch(fetchActivePracticeProvidersData({ productId: String(practiceId), onlyActiveRecords: true }));
      dispatch(fetchPracticeUserDataByPracticeId(practiceId));
    }
  }, [practiceId])

  const practiceProvidersOptions = useMemo(() => {
    let options: { label: string; value: number }[] = [];

    practiceProvidersInfo?.map((option: any) => {
      options.push({
        label: `${option.title ? option.title : ""} ${option.firstName ? option.firstName : ""} ${option.middleName ? option.middleName + " " : ""
          }${option.lastName ? option.lastName : ""}`,
        value: option.providerId,
      });
    });

    // CS-1530 - Point 5 Add "All Providers" once at the end
    options.push({
      label: "All Providers",
      value: 0,
    });

    return options;
  }, [practiceProvidersInfo]);

  const practiceUserOptions = useMemo(() => {
    let options: { label: string; value: number }[] = [];
    practiceUsersData?.map((option: any) => {
      options.push({
        label: `${option.email ? option.email : ""} (${option.userRole ? option.userRole : ""})`,
        value: option.userId,
      });
    });
    return options;
  }, [practiceUsersData]);

  // watch the dynamic array so filtering reacts to changes
  const usersArray = watch('users'); // [{ userId, isNotificationEnabled }, ...] [2]

  // helper to build filtered options per row
  const getFilteredOptions = (rowIndex: number) => {
    const selected = new Set(
      (usersArray || [])
        .map((u: any) => u?.userId)
        .filter((v: any, i: number) => v != null && i !== rowIndex) // exclude current row from the set
    );

    const currentValue = usersArray?.[rowIndex]?.userId ?? null;

    return practiceUserOptions.filter((opt: any) =>
      opt.value === currentValue || !selected.has(opt.value)
    );
  };


  const SwitchTextTrack = styled(Switch)({
    width: 74,
    height: 42,
    padding: 8,
    [`& .${switchClasses.switchBase}`]: {
      padding: 11,
      color: "#ff6a00",
    },
    [`& .${switchClasses.thumb}`]: {
      width: 20,
      height: 20,
      backgroundColor: "#fff",
    },
    [`& .${switchClasses.track}`]: {
      background:
        "linear-gradient(to right,rgb(161, 161, 161),rgb(84, 84, 84))",
      opacity: "1 !important",
      borderRadius: 20,
      position: "relative",
      "&:before, &:after": {
        display: "inline-block",
        position: "absolute",
        top: "50%",
        width: "50%",
        transform: "translateY(-50%)",
        color: "#fff",
        textAlign: "center",
        fontSize: "0.75rem",
        fontWeight: 500,
      },
      "&:before": {
        content: '"ON"',
        left: 4,
        opacity: 0,
      },
      "&:after": {
        content: '"OFF"',
        right: 4,
      },
    },
    [`& .${switchClasses.checked}`]: {
      [`&.${switchClasses.switchBase}`]: {
        color: "#185a9d",
        transform: "translateX(32px)",
        "&:hover": {
          backgroundColor: "rgba(24,90,257,0.08)",
        },
      },
      [`& .${switchClasses.thumb}`]: {
        backgroundColor: "#fff",
      },
      [`& + .${switchClasses.track}`]: {
        background: "linear-gradient(to right,rgb(0, 196, 88),rgb(0, 97, 29))",
        "&:before": {
          opacity: 1,
        },
        "&:after": {
          opacity: 0,
        },
      },
    },
  });


  const onSubmit = (data: any) => {
    const payload = {
      practiceId,
      providerId: data.providerId ?? null,
      authorisedUserList: (data.users || []).filter((u: any) => u?.userId != null),
    };

    if (!payload.authorisedUserList.length) {
      dispatch(setSingleAlertObj({ message: 'Please select at least one user email.', alertType: 'error' }));
      return;
    }

    if (mode === 'edit' && data.providerAuthorizedUserId) {
      onUpdate(data.providerAuthorizedUserId, payload); // call update API
    } else {
      onCreate(payload); // call create API
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      keepMounted
      fullScreen={isMobile}
      scroll="paper"
      PaperProps={{ sx: paperSx(isMobile) }}
      onClick={(event) => {
        event.stopPropagation();
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box sx={headerSx}>
          <Box sx={iconBoxSx(tokens.accent)}>
            <Users size={20} color={tokens.accent} strokeWidth={1.8} />
          </Box>
          <Box sx={{ flex: 1, pr: 4 }}>
            <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
              {mode === 'edit' ? 'Edit Authorised User' : 'Add Authorised User'}
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
              Select provider, assign users, and configure notifications.
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={closeIconSx} aria-label="Close">
            <Close sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>
        <Box
          sx={{
            ...bodySx,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            scrollbarGutter: "stable",
            "&::-webkit-scrollbar": { width: 6, height: 6 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: isDark ? "rgba(255,255,255,0.12)" : "#CBD5E1",
              borderRadius: 4,
            },
          }}
        >
          <Grid container spacing={2}>
              {/* Provider */}
              <Grid item xs={12}>
                <Box>
                  <FieldLabel label="Provider" required tokens={tokens} />
                  <Box sx={fieldWrapSx()}>
                    <CustomDropdownField
                      name="providerId"
                      control={control}
                      options={practiceProvidersOptions}
                      label=""
                      sortOptions={false}
                      rules={{ required: 'Select a provider.' }}
                    />
                  </Box>
                </Box>
              </Grid>

              {/* Section header row (aligned with body rows) */}
              <Grid
                item
                xs={12}
                sx={{ mt: 1 }}
              >
                <Grid
                  container
                  alignItems="center"
                  columnSpacing={2}
                  rowSpacing={1}
                >
                  <Grid item xs={12} sm={7} md={5}>
                  </Grid>
                  <Grid item xs={8} sm={3} md={3}>
                  </Grid>
                  {mode !== 'edit' && (
                    <Grid
                      item
                      xs={4}
                      sm={2}
                      md={4}
                      sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}
                    >
                      <Box
                        component="button"
                        type="button"
                        onClick={() => append({ userId: null, isNotificationEnabled: false })}
                        sx={{
                          ...primaryBtnSx,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.8,
                          px: 2,
                          py: 0.8,
                          border: "none",
                          cursor: "pointer",
                          color: "#fff",
                        }}
                      >
                        <AddCircleOutline sx={{ fontSize: "1rem" }} />
                        Add Row
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Grid>

              {/* Dynamic rows */}
              <Grid item xs={12}>
                {fields.map((field, index) => (
                  <Box
                    key={field.id}
                    sx={{
                      mb: 1.5,
                      p: 1.5,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Grid container alignItems="center" columnSpacing={2} rowSpacing={1}>
                      {/* User Email dropdown (grows) */}
                      <Grid item xs={12} sm md={7}>
                        <Box>
                          <FieldLabel label="User Email" required tokens={tokens} />
                          <Box sx={fieldWrapSx()}>
                            <CustomDropdownField
                              name={`users.${index}.userId`}
                              control={control}
                              options={getFilteredOptions(index)}
                              label=""
                              rules={
                                getValues('providerId') !== undefined
                                  ? { required: 'Select at least one user email.' , validate: (v: string | number) => (practiceUserOptions?.some(o => o.value === Number(v)) ? true : 'Select at least one user email.') }
                                  : {}
                              }
                            />
                          </Box>
                        </Box>
                      </Grid>

                      {/* Inline label + Switch */}
                      <Grid
                        item
                        xs={12} sm md={4}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Email Notification:
                        </Typography>
                        <Controller
                          name={`users.${index}.isNotificationEnabled`}
                          control={control}
                          render={({ field }) => (
                            <SwitchTextTrack
                              checked={!!field.value}
                              onChange={(_, v) => field.onChange(v)}
                              inputProps={{ 'aria-label': 'Email Notification' }}
                            />
                          )}
                        />
                      </Grid>

                      {/* Delete button at far right */}
                      <Grid
                        item
                        xs={12} sm md={1}
                        // sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}
                      >
                        {fields.length > 1 && (
                          <CustomIconButton
                            color="error"
                            size="small"
                            variant="outlined"
                            tooltipMessage="Delete"
                            tooltipPlacement="top"
                            onClick={() => remove(index)}
                          >
                            <Delete fontSize="small" />
                          </CustomIconButton>
                          // <IconButton
                          //   aria-label="Remove user"
                          //   color="error"
                          //   onClick={() => remove(index)}
                          //   edge="end"
                          // >
                          //   <DeleteOutline />
                          // </IconButton>
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Grid>

            </Grid>
        </Box>

        <Box sx={footerSx}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Box
              component="button"
              type="submit"
              sx={{
                ...primaryBtnSx,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.8,
                px: 3,
                py: 1.2,
                border: "none",
                cursor: "pointer",
                color: "#fff",
                order: { xs: 1, sm: 2 },
              }}
            >
              <EditOutlined sx={{ fontSize: "1.1rem" }} />
              {mode === 'edit' ? 'Edit' : 'Save'}
            </Box>
            <Box
              component="button"
              type="button"
              onClick={onClose}
              sx={{
                ...cancelBtnSx,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.8,
                px: 3,
                py: 1.2,
                border: "1px solid",
                cursor: "pointer",
                order: { xs: 2, sm: 1 },
              }}
            >
              Cancel
            </Box>
          </Box>
        </Box>
      </Box>
    </Dialog>

    // <Dialog open={open} TransitionComponent={Transition} keepMounted fullWidth
    //   onClose={onClose} aria-describedby="alert-dialog-slide-description"
    //   onClick={(event) => {
    //     event.stopPropagation();
    //   }}
    // >
    //   <form onSubmit={handleSubmit(onSubmit)}>
    //     <DialogTitle>Add Authorised User</DialogTitle>
    //     <DialogContent>
    //       <Box p={1}>
    //         <Grid container spacing={2}>
    //           <Grid item md={12} xs={12}>
    //             <CustomDropdownField
    //               name="providerId"
    //               control={control}
    //               options={practiceProvidersOptions}
    //               label="Provider"
    //             />
    //           </Grid>
    //           <Grid item md={12} xs={12}>
    //           <CustomMultipleDropDown
    //             name="authorisedUserList"
    //             control={control}
    //             options={practiceUserOptions}
    //             label="User Emails"
    //             sortOptions={true}
    //             rules={getValues("providerId") !== undefined ? { required: "Select at least one user email." } : {}}
    //             onChangeSelect={() => { }}
    //           />
    //           </Grid>
    //           <Grid item md={12} xs={12}>
    //           <Controller
    //             name="isNotificationEnabled"
    //             control={control}
    //             render={({ field }) => (
    //               <FormControlLabel
    //                 control={
    //                   <SwitchTextTrack
    //                     {...field}
    //                     name={field.name}
    //                     checked={field.value}
    //                     size="medium"
    //                     onChange={(e) => {
    //                       const isChecked = e.target.checked;
    //                       field.onChange(isChecked);
    //                     }}
    //                   />
    //                 }
    //                 label="Email Notification"
    //                 labelPlacement="start"
    //               />
    //             )}
    //           />
    //         </Grid>
    //         </Grid>
    //       </Box>
    //     </DialogContent>
    //     <DialogActions>
    //       <Grid container justifyContent="flex-end" sx={{ mt: 1, flexWrap: "wrap" }} spacing={1} >
    //         <Grid item xs={12} sm="auto">
    //           <Button type="submit" startIcon={<AddCircleOutline />} fullWidth >
    //             Save
    //           </Button>
    //         </Grid>
    //         <Grid item xs={12} sm="auto">
    //           <Button color="error" onClick={onClose} startIcon={<Cancel />} fullWidth >
    //             Cancel
    //           </Button>
    //         </Grid>
    //       </Grid>
    //     </DialogActions>
    //   </form>
    // </Dialog>
  );
};

export default AuthorizedUserPopup;


